import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Copy, Gift, Link2, Sparkles, Upload } from "lucide-react";
import { usePrivatePage } from "../lib/seo";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { AI_SAMPLES, SAMPLES, SPORT_SAMPLES } from "../lib/samples";
import { AI_EXAMPLES } from "../lib/aiPrompt";
import { PALETTES, SIZES, eur, sizeOf } from "../lib/shield";
import { countyBadgeUrl, provinceBadgeUrl } from "../lib/regions";

// Animated scenes for social clips (scripts/record_social.py drives window.__scene(i)) and still Christmas images.
const withColours = (d, id) => { const { id: _, name, ...c } = PALETTES.find((p) => p.id === id); return { ...d, colours: c }; };
const named = (d, name, rating) => ({ ...d, player: { ...d.player, name, rating: String(rating) } });
const SQUAD = [["Aoife Kelly", 84], ["Saoirse Byrne", 81], ["Niamh Walsh", 88], ["Ciara Doyle", 79], ["Róisín Ryan", 86], ["Éabha Nolan", 83]].map(([n, r]) => named(SAMPLES[0], n, r));
const MIX = [SAMPLES[0], SPORT_SAMPLES.hurling[1], SAMPLES[1], SPORT_SAMPLES.rugby[1], SPORT_SAMPLES.hurling[0], SAMPLES[2]];

const Shield = ({ d, className = "", style }) => <div className={`[filter:drop-shadow(0_24px_30px_rgba(0,0,0,.5))] ${className}`} style={style}><ShieldPreview design={d} /></div>;
const Head = ({ children, sub }) => (
  <div className="animate-[fadeUp_.6s_ease-out_both] px-20 text-center">
    <h1 className="font-display text-[104px] font-black uppercase leading-[.92] tracking-tight">{children}</h1>
    {sub && <p className="mt-8 text-[44px] font-semibold leading-tight text-slate-300">{sub}</p>}
  </div>
);
const Lime = ({ children }) => <span className="text-lime-300">{children}</span>;
const Big = ({ d }) => <Shield d={d} className="w-[600px] animate-[fadeUp_.6s_ease-out_both]" />;

const Cycling = ({ d }) => {
  const ids = ["green-white", "navy-gold", "red-white", "maroon-white", "blue-white"];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((v) => v + 1), 800); return () => clearInterval(t); }, []);
  return <Shield d={withColours(d, ids[i % ids.length])} className="w-[560px]" />;
};

const Fan = ({ list, w = 350 }) => (
  <div className="flex items-end justify-center">
    {list.map((d, i) => {
      const mid = (list.length - 1) / 2;
      return <Shield key={i} d={d} className="animate-[fadeUp_.7s_ease-out_both]"
        style={{ width: w, animationDelay: `${i * 0.15}s`, transform: `rotate(${(i - mid) * 8}deg) translateY(${i === Math.round(mid) ? -40 : 0}px)`, margin: "0 -12px", zIndex: i }} />;
    })}
  </div>
);

const Sizes = ({ d = SAMPLES[0] }) => (
  <div className="flex items-end justify-center gap-6">
    {SIZES.map((s, i) => (
      <div key={s.id} className="animate-[fadeUp_.5s_ease-out_both] text-center" style={{ animationDelay: `${i * 0.2}s` }}>
        <Shield d={d} style={{ width: `${(sizeOf(s.id).h / 840) * 360}px` }} />
        <div className="font-display mt-4 text-4xl font-black uppercase">{s.id === "xl" ? "XL" : s.name}</div>
        <div className="font-cond text-5xl font-bold text-lime-300">{eur(s.price).replace(".00", "")}</div>
      </div>
    ))}
  </div>
);

const Grid = ({ list = SQUAD }) => (
  <div className="grid grid-cols-3 gap-6 px-16">
    {list.map((d, i) => <Shield key={i} d={d} className="animate-[fadeUp_.5s_ease-out_both]" style={{ animationDelay: `${i * 0.12}s` }} />)}
  </div>
);

const Chat = () => (
  <div className="mx-20 animate-[fadeUp_.6s_ease-out_both] rounded-[40px] bg-[#0b141a] p-10 shadow-2xl">
    <div className="text-3xl font-bold text-slate-400">U12 Girls · Team chat</div>
    <div className="mt-8 max-w-[85%] rounded-3xl rounded-tl-none bg-[#005c4b] p-8 text-[40px] leading-snug">
      Hi all! Add your daughter's photo and stats for her player shield here
      <div className="mt-4 flex items-center gap-3 font-bold text-lime-300"><Link2 className="h-10 w-10" /> sportsshields.ie/s/u12girls</div>
    </div>
    <div className="ml-auto mt-6 max-w-[60%] rounded-3xl rounded-tr-none bg-[#202c33] p-7 text-[38px]">Done! She loves it</div>
  </div>
);

const Ticks = ({ items }) => (
  <ul className="space-y-8 px-24">
    {items.map((t, i) => (
      <li key={t} className="flex animate-[fadeUp_.5s_ease-out_both] items-center gap-6 text-[50px] font-bold" style={{ animationDelay: `${i * 0.25}s` }}>
        <span className="flex h-20 w-20 shrink-0 items-center justify-center bg-lime-300 text-slate-900"><Check className="h-12 w-12" strokeWidth={3} /></span>{t}
      </li>
    ))}
  </ul>
);

const Cta = ({ line, extra }) => (
  <div className="flex animate-[fadeUp_.6s_ease-out_both] flex-col items-center gap-12">
    <img src="/img/logo-mark.png" alt="" className="h-64 w-auto" />
    <div className="font-display text-center text-[96px] font-black uppercase leading-none">{line}</div>
    {extra}
    <div className="bg-lime-300 px-12 py-6 font-display text-[64px] font-black text-slate-900">sportsshields.ie</div>
  </div>
);

const Wrapped = ({ d }) => (
  <div className="relative w-[560px] animate-[fadeUp_.6s_ease-out_both]">
    <Shield d={d} />
    <div className="absolute inset-y-0 left-1/2 w-12 -translate-x-1/2 bg-red-600/90 shadow-lg" />
    <div className="absolute left-0 right-0 top-[46%] h-12 bg-red-600/90 shadow-lg" />
    <Gift className="absolute left-1/2 top-[46%] h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600 p-5 text-white shadow-2xl" />
  </div>
);
const OrderBy = () => (
  <div className="animate-[fadeUp_.6s_ease-out_both] border-8 border-lime-300 px-16 py-12 text-center">
    <div className="font-display text-[60px] font-black uppercase">Order by</div>
    <div className="font-display text-[170px] font-black leading-none text-lime-300">10 Dec</div>
    <div className="mt-4 text-[42px] font-semibold text-slate-200">for Christmas delivery</div>
  </div>
);

const Badges = ({ urls, cols }) => (
  <div className={`grid gap-8 px-16 ${cols === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
    {urls.map((u, i) => <img key={u} src={u} alt="" className="w-full animate-[fadeUp_.5s_ease-out_both] border-4 border-white/80 shadow-2xl" style={{ animationDelay: `${i * 0.15}s`, aspectRatio: "3 / 2" }} />)}
  </div>
);
const PromptCard = () => (
  <div className="mx-20 animate-[fadeUp_.6s_ease-out_both] border-4 border-white bg-white p-10 text-left text-slate-900 shadow-[16px_16px_0_0_#a3e635]">
    <div className="flex items-center gap-4 font-display text-[44px] font-black uppercase"><Sparkles className="h-14 w-14 text-fuchsia-500" /> Get creative with AI</div>
    <div className="mt-6 space-y-3 font-mono text-[30px] leading-snug text-slate-700">
      <p>Create a bold, vibrant background for a personalised football player shield…</p>
      <p>Theme: <span className="bg-fuchsia-200 px-2 font-bold text-slate-900">Lightning</span></p>
      <p>Main colours: <span className="bg-amber-200 px-2 font-bold text-slate-900">Red, white & gold</span></p>
    </div>
    <div className="mt-8 flex gap-5">
      <span className="flex animate-pulse items-center gap-3 bg-slate-900 px-7 py-4 text-[34px] font-bold text-white"><Copy className="h-9 w-9" /> Copy AI Prompt</span>
      <span className="flex items-center gap-3 bg-lime-400 px-7 py-4 text-[34px] font-bold"><Upload className="h-9 w-9" /> Upload</span>
    </div>
  </div>
);
const Backdrops = () => (
  <div className="grid grid-cols-3 gap-8 px-16">
    {AI_EXAMPLES.map((e, i) => (
      <div key={e.id} className="animate-[fadeUp_.5s_ease-out_both] text-center" style={{ animationDelay: `${i * 0.25}s` }}>
        <img src={e.src} alt="" className="aspect-[2/3] w-full border-4 border-white/80 object-cover shadow-2xl" />
        <div className="font-display mt-4 text-[34px] font-black uppercase">{e.name}</div>
      </div>
    ))}
  </div>
);
const Rotating = ({ list }) => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((v) => v + 1), 1000); return () => clearInterval(t); }, []);
  return <Shield d={list[i % list.length]} className="w-[560px]" />;
};

const COUNTY_SCENE = [<Head key="h" sub="All 32 counties – plus London, New York & more">Your <Lime>county</Lime> colours</Head>,
  <Badges key="b" urls={["gal", "kik", "cor", "tip", "lim", "dub"].map(countyBadgeUrl)} />];
const PROVINCE_SCENE = [<Head key="h" sub="Munster, Leinster, Ulster or Connacht">Fly your <Lime>province</Lime></Head>,
  <Badges key="b" cols={2} urls={["mun", "lei", "uls", "con"].map(provinceBadgeUrl)} />];

const sportScenes = (list, hook, statsLine, regionScene) => [
  [hook, <Fan key="f" list={list} />],
  [<Head key="h" sub="We remove the background for you">Upload their <Lime>photo</Lime></Head>, <Big key="s" d={list[1]} />],
  regionScene || [<Head key="h" sub={statsLine}>In your <Lime>club colours</Lime></Head>, <Cycling key="c" d={list[0]} />],
  [<Head key="h" sub="Small, Medium, Large or XL">Sizes from <Lime>€16</Lime></Head>, <Sizes key="z" d={list[0]} />],
  [<Head key="h" sub="Delivered in 7–10 working days">Printed in <Lime>Ireland</Lime></Head>, <Big key="s" d={list[list.length - 1]} />],
  [<Cta key="c" line={<>Design <Lime>yours</Lime> today</>} />],
];

const SCENES = {
  parents: sportScenes(SAMPLES, <Head key="h">Turn your child into a <Lime>legend</Lime></Head>, "Name, position, rating and six stats"),
  hurling: sportScenes(SPORT_SAMPLES.hurling, <Head key="h" sub="Personalised player shields">Hurling & camogie <Lime>legends</Lime></Head>, "Stick skill, pace, work rate and more", COUNTY_SCENE),
  rugby: sportScenes(SPORT_SAMPLES.rugby, <Head key="h" sub="Personalised player shields">Got a rugby <Lime>star?</Lime></Head>, "Tackling, handling, kicking and more", PROVINCE_SCENE),
  coaches: [
    [<Head key="h" sub="Personalised player shields for every child">Fundraise with your <Lime>squad</Lime></Head>, <Fan key="f" list={SQUAD.slice(0, 3)} />],
    [<Head key="h" sub="Every player matches automatically">Set the club look <Lime>once</Lime></Head>, <Grid key="g" />],
    [<Head key="h">Share <Lime>one link</Lime></Head>, <Chat key="c" />],
    [<Head key="h">Parents do <Lime>the rest</Lime></Head>, <Ticks key="t" items={["Add the photo & stats", "Pick a size", "Pay for their own shield"]} />],
    [<Head key="h" sub="No upfront cost. No stock. No hassle.">Free for <Lime>your club</Lime></Head>, <Ticks key="t" items={["Your club earns on every order", "Track every shield online", "Printed & delivered in Ireland"]} />],
    [<Cta key="c" line={<>Sign up <Lime>your club</Lime></>} />],
  ],
  aibg: [
    [<Head key="h" sub="A background nobody else has">Get creative <Lime>with AI</Lime></Head>, <Fan key="f" list={AI_SAMPLES} />],
    [<Head key="h" sub="Tap the sparkle in our designer">Copy our <Lime>AI prompt</Lime></Head>, <PromptCard key="p" />],
    [<Head key="h" sub="Or your favourite team's colours">Pick <Lime>any theme</Lime></Head>, <Backdrops key="b" />],
    [<Head key="h" sub="Paste into ChatGPT or Gemini, then upload">Upload your <Lime>background</Lime></Head>, <Big key="s" d={AI_SAMPLES[0]} />],
    [<Head key="h" sub="Be as creative as you like">No <Lime>limits</Lime></Head>, <Rotating key="r" list={AI_SAMPLES} />],
    [<Cta key="c" line={<>Get <Lime>creative</Lime> today</>} />],
  ],
  christmas: [
    [<Head key="h" sub="For the young player in your life">The perfect <Lime>Christmas gift</Lime></Head>, <Wrapped key="w" d={SAMPLES[0]} />],
    [<Head key="h" sub="Their photo, name, club colours & stats">Their very own <Lime>player shield</Lime></Head>, <Fan key="f" list={[SPORT_SAMPLES.hurling[1], SAMPLES[1], SPORT_SAMPLES.rugby[1]]} />],
    [<Head key="h">Every <Lime>sport</Lime></Head>, <Grid key="g" list={MIX} />],
    [<Head key="h" sub="Small, Medium, Large or XL">Sizes from <Lime>€16</Lime></Head>, <Sizes key="z" />],
    [<Head key="h" sub="Printed in Ireland">Don't <Lime>miss out</Lime></Head>, <OrderBy key="o" />],
    [<Cta key="c" line={<>Happy <Lime>Christmas!</Lime></>} extra={<div className="text-[44px] font-semibold text-slate-200">Order by 10 December</div>} />],
  ],
};

const STILLS = {
  "xmas-post": { w: 1080, h: 1080 },
  "xmas-story": { w: 1080, h: 1920 },
  "early-post": { w: 1080, h: 1080 },
  "early-story": { w: 1080, h: 1920 },
  "early-cover": { w: 1640, h: 624 },
  "early-link": { w: 1200, h: 630 },
  "gift-post": { w: 1080, h: 1080 },
  "gift-story": { w: 1080, h: 1920 },
  "gift-cover": { w: 1640, h: 624 },
  "gift-link": { w: 1200, h: 630 },
};

const GiftShield = ({ d, w, style }) => (
  <div className="relative shrink-0" style={{ width: w, margin: "0 -10px", ...style }}>
    <Shield d={d} />
    <div className="absolute inset-y-[3%] left-1/2 -translate-x-1/2 bg-red-600/90 shadow-lg" style={{ width: w * 0.08 }} />
    <div className="absolute left-[2%] right-[2%] top-[44%] bg-red-600/90 shadow-lg" style={{ height: w * 0.08 }} />
    <Gift className="absolute left-1/2 top-[44%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600 text-white shadow-2xl ring-4 ring-amber-300" style={{ width: w * 0.24, height: w * 0.24, padding: w * 0.04 }} />
  </div>
);
const GiftFan = ({ w }) => {
  const list = [SPORT_SAMPLES.hurling[1], SAMPLES[1], SPORT_SAMPLES.rugby[1]];
  return <div className="flex items-end justify-center">{list.map((d, i) => <GiftShield key={i} d={d} w={w} style={{ transform: `rotate(${(i - 1) * 8}deg) translateY(${i === 1 ? -w * 0.1 : 0}px)`, zIndex: i === 1 ? 3 : i }} />)}</div>;
};
const Lights = () => (
  <div className="absolute inset-x-0 top-4 flex justify-around">{Array.from({ length: 18 }, (_, i) => (
    <span key={i} className="h-6 w-4 rounded-b-full shadow-[0_0_24px_6px_currentColor]" style={{ color: ["#ef4444", "#facc15", "#a3e635", "#38bdf8"][i % 4], background: "currentColor", marginTop: i % 2 ? 18 : 6 }} />))}</div>
);
const EARLY = {
  post: { wrap: "flex-col gap-6 pt-10", h: "text-[110px]", sub: "text-[40px]", fan: 270, tag: "text-[32px]", pill: "px-8 py-3 text-[38px]" },
  story: { wrap: "flex-col gap-14", h: "text-[150px]", sub: "text-[54px]", fan: 340, tag: "text-[44px]", pill: "px-12 py-5 text-[56px]" },
  cover: { wrap: "flex-row gap-16 px-[220px] pt-6", h: "text-[104px]", sub: "text-[34px]", fan: 230, tag: "text-[28px]", pill: "px-7 py-3 text-[34px]", row: true },
  link: { wrap: "flex-row gap-10 px-16 pt-6", h: "text-[92px]", sub: "text-[30px]", fan: 200, tag: "text-[24px]", pill: "px-6 py-2.5 text-[30px]", row: true },
};
const VoucherVisual = ({ w }) => (
  <div className="relative shrink-0" style={{ width: w * 2.3, height: w * 1.6 }}>
    <GiftShield d={SAMPLES[1]} w={w} style={{ position: "absolute", right: 0, top: 0, transform: "rotate(8deg)" }} />
    <div className="absolute bottom-0 left-0 border-4 border-slate-900 bg-white text-left text-slate-900 shadow-[10px_10px_0_0_#a3e635]" style={{ width: w * 1.55, padding: w * 0.09, transform: "rotate(-6deg)" }}>
      <div className="font-black uppercase text-lime-700" style={{ fontSize: w * 0.075 }}>Sports Shields</div>
      <div className="font-display font-black uppercase leading-none" style={{ fontSize: w * 0.15 }}>Gift voucher</div>
      <div className="font-display font-black text-red-600 leading-none" style={{ fontSize: w * 0.3, marginTop: w * 0.04 }}>€40</div>
      <div className="font-mono font-black border-2 border-dashed border-slate-900 bg-lime-50 text-center" style={{ fontSize: w * 0.085, marginTop: w * 0.05, padding: w * 0.03 }}>GIFT-XMAS-2026</div>
    </div>
  </div>
);
const COPY = {
  early: { h: <>Order <Lime>early</Lime></>, sub: <>for Christmas delivery<br /><span className="text-red-400">to avoid disappointment</span></>, tag: "Personalised player shields · from €16" },
  gift: { h: <>Stuck for <Lime>a gift?</Lime></>, sub: <>Give a shield gift voucher<br /><span className="text-red-400">they design their own</span></>, tag: "Any amount €20–€200 · never expires" },
};
const EarlyStill = ({ v, theme = "early" }) => {
  const c = EARLY[v];
  const t = COPY[theme];
  const text = (
    <div className={`flex flex-col ${c.row ? "items-start text-left" : "items-center text-center"} gap-5`}>
      <div className={`font-display font-black uppercase leading-[.88] tracking-tight ${c.h}`}>{t.h}</div>
      <div className={`font-display font-extrabold uppercase leading-tight ${c.sub}`}>{t.sub}</div>
      <div className={`whitespace-nowrap font-semibold text-slate-200 ${c.tag}`}>{t.tag}</div>
      <div className={`bg-lime-300 font-display font-black uppercase text-slate-900 ${c.pill}`}>{theme === "gift" ? "sportsshields.ie/gift" : "sportsshields.ie"}</div>
    </div>
  );
  return (
    <div className={`relative flex flex-1 items-center justify-center ${c.wrap}`}>
      <Lights />
      {v === "story" && theme === "early" && <Gift className="h-36 w-36 rounded-full bg-red-600 p-6 text-white shadow-2xl ring-8 ring-amber-300" />}
      {text}
      {theme === "gift" ? <VoucherVisual w={c.fan * 1.05} /> : <GiftFan w={c.fan} />}
    </div>
  );
};

const Snow = () => <div className="pointer-events-none absolute inset-0 animate-[snow_9s_linear_infinite] opacity-70 [background-image:radial-gradient(circle,#fff_2.5px,transparent_3px),radial-gradient(circle,#fff_1.5px,transparent_2px)] [background-size:140px_140px,90px_90px] [background-position:0_0,45px_60px]" />;

const XmasStill = ({ story }) => (
  <div className={`relative flex flex-1 flex-col items-center justify-center ${story ? "gap-20" : "gap-8"} text-center`}>
    {story && <Gift className="h-40 w-40 rounded-full bg-red-600 p-7 text-white shadow-2xl" />}
    <div className={`font-display font-black uppercase leading-[.9] ${story ? "text-[100px]" : "text-[84px]"}`}>The perfect<br /><Lime>Christmas gift</Lime></div>
    <div className={story ? "py-10" : "scale-[.62] -my-28"}><Fan list={[SPORT_SAMPLES.hurling[1], SAMPLES[1], SPORT_SAMPLES.rugby[1]]} w={story ? 360 : 300} /></div>
    <div className={`font-semibold text-slate-100 ${story ? "text-[46px]" : "text-[34px]"}`}>Personalised player shields · from €16</div>
    <div className={`bg-lime-300 font-display font-black uppercase text-slate-900 ${story ? "px-12 py-6 text-[56px]" : "px-8 py-4 text-[40px]"}`}>Order by 10 December</div>
    <div className={`font-display font-black ${story ? "text-[52px]" : "text-[36px]"}`}>sportsshields.ie</div>
  </div>
);

export default function Promo() {
  usePrivatePage("Promo");
  const { kind } = useParams();
  const [i, setI] = useState(0);
  useEffect(() => { window.__scene = setI; }, []);
  const still = STILLS[kind];
  const xmas = kind === "christmas" || !!still;
  const [theme, early] = /^(early|gift)-/.test(kind || "") ? kind.split("-") : [null, null];
  const size = still || { w: 1080, h: 1920 };
  const scenes = SCENES[kind] || SCENES.parents;
  const [head, body] = scenes[Math.min(i, scenes.length - 1)];
  return (
    <div className={`relative flex flex-col overflow-hidden text-white ${xmas ? "bg-[#0c2a1f]" : "bg-slate-950"}`} style={{ width: size.w, height: size.h }} data-testid="promo-stage">
      <div className={`h-4 ${xmas ? "bg-[repeating-linear-gradient(90deg,#dc2626_0_40px,#fff_40px_80px)]" : "stripe-rule"}`} />
      <div className={`absolute -right-40 top-1/3 h-[900px] w-[900px] rounded-full blur-3xl ${xmas ? "bg-red-500/20" : "bg-lime-400/10"}`} />
      {xmas && <Snow />}
      {early ? <EarlyStill v={early} theme={theme} /> : still ? <XmasStill story={kind === "xmas-story"} /> : (
        <>
          <div key={i} className="relative flex flex-1 flex-col items-center justify-center gap-24 pb-56">{head}{body}</div>
          <div className="relative flex items-center justify-center pb-16"><img src="/img/logo.png" alt="" className="h-14 w-auto brightness-0 invert" /></div>
        </>
      )}
      <div className="hidden">{SQUAD.concat(SAMPLES, MIX, AI_SAMPLES).map((d, k) => <ShieldPreview key={k} design={d} />)}</div>
    </div>
  );
}
